import { useEffect, useState } from "react";
import { Envelope, MagnifyingGlass } from "@phosphor-icons/react";
import { formatET } from "../lib/dateUtils";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";
import { getNotificationBadge } from "../lib/notificationTypes";

const navItems = [{ to: "/admin/notifications", label: "Notification Center", icon: <Envelope size={18} weight="duotone" />, testId: "nav-admin-notifications" }];
const PAGE_SIZE = 20;

export default function AdminNotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchWindow = async (count) => {
    try {
      const { data } = await api.get("/admin/notifications", { params: { limit: count, skip: 0, unread_only: filter === "unread", search: debouncedSearch } });
      setUnreadCount(data.unread_count);
      setTotal(data.total);
      setNotifications(data.notifications);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get("/admin/notifications", { params: { limit: PAGE_SIZE, skip: notifications.length, unread_only: filter === "unread", search: debouncedSearch } });
      setUnreadCount(data.unread_count);
      setTotal(data.total);
      setNotifications([...notifications, ...data.notifications]);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchWindow(PAGE_SIZE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, debouncedSearch]);

  const markRead = async (id) => {
    await api.patch(`/admin/notifications/${id}/read`);
    fetchWindow(Math.max(notifications.length, PAGE_SIZE));
  };

  const markAllRead = async () => {
    await api.patch("/admin/notifications/read-all");
    fetchWindow(Math.max(notifications.length, PAGE_SIZE));
  };

  const hasMore = notifications.length < total;

  return (
    <DashboardLayout navItems={navItems} moduleName="Notification Center" moduleBackPath="/admin">
      <div data-testid="admin-notifications-page">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-1">Notification Center</h1>
            <p className="text-igc-muted text-sm" data-testid="admin-notifications-unread-summary">
              {debouncedSearch || filter === "unread"
                ? `${total} notification${total === 1 ? "" : "s"} match${total === 1 ? "es" : ""} this view (${unreadCount} unread overall).`
                : `Every client inquiry, message, and system alert lands here — ${unreadCount} unread.`}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-igc-muted" />
              <Input
                data-testid="notif-search-input"
                placeholder="Search by client ID or keyword..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                title="Type any keyword or client ID to instantly filter the notifications below."
                className="bg-igc-surfaceLight border-igc-gold/20 pl-9 w-64"
              />
            </div>
            <Button data-testid="notif-filter-all-button" onClick={() => setFilter("all")} title="Shows every notification, read or unread." className={filter === "all" ? "bg-igc-gold text-black" : "bg-igc-surfaceLight text-igc-cream hover:bg-igc-surfaceLight/70"}>All</Button>
            <Button data-testid="notif-filter-unread-button" onClick={() => setFilter("unread")} title="Shows only notifications you haven't marked read yet." className={filter === "unread" ? "bg-igc-gold text-black" : "bg-igc-surfaceLight text-igc-cream hover:bg-igc-surfaceLight/70"}>Unread</Button>
            <Button data-testid="notif-mark-all-read-button" onClick={markAllRead} title="Marks every notification as read in one click." className="bg-igc-surfaceLight text-igc-cream hover:bg-igc-surfaceLight/70">Mark All Read</Button>
          </div>
        </div>
        <div className="space-y-2" data-testid="admin-notifications-list">
          {loading && <p className="text-igc-muted text-sm" data-testid="admin-notifications-loading">Loading notifications...</p>}
          {!loading && error && (
            <p className="text-igc-danger text-sm" data-testid="admin-notifications-error">
              Couldn't load notifications. <button onClick={() => fetchWindow(PAGE_SIZE)} className="underline hover:text-igc-goldText">Try again</button>
            </p>
          )}
          {!loading && !error && notifications.length === 0 && (
            <p className="text-igc-muted text-sm" data-testid="admin-notifications-empty">
              {debouncedSearch ? `No notifications match "${debouncedSearch}".` : "No notifications."}
            </p>
          )}
          {!loading && !error && notifications.map((n) => (
            <div key={n.id} data-testid={`admin-notification-row-${n.id}`} className={`bg-igc-surface border p-4 flex items-start justify-between gap-4 ${n.read ? "border-igc-gold/10" : "border-igc-gold/30"}`}>
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span data-testid={`notification-type-badge-${n.id}`} className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${getNotificationBadge(n.type).cls}`}>
                    {getNotificationBadge(n.type).label}
                  </span>
                </div>
                <p className="text-igc-cream text-sm">{n.message}</p>
                <p className="text-igc-muted text-xs mt-1">{formatET(n.timestamp, "MMM d, yyyy h:mm a")}{n.client_id ? ` — ${n.client_id}` : ""}</p>
              </div>
              {!n.read && (
                <button data-testid={`mark-read-${n.id}`} onClick={() => markRead(n.id)} title="Marks this one notification as read." className="text-xs text-igc-goldText hover:text-igc-goldHover whitespace-nowrap">Mark Read</button>
              )}
            </div>
          ))}
          {!loading && !error && hasMore && (
            <div className="pt-4 text-center">
              <Button data-testid="notif-load-more-button" onClick={loadMore} disabled={loadingMore} className="bg-igc-surfaceLight text-igc-cream hover:bg-igc-surfaceLight/70">
                {loadingMore ? "Loading..." : `Load More (${notifications.length} of ${total})`}
              </Button>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
